import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography,
  CircularProgress, Chip, Divider, Avatar,
} from '@mui/material';
import PeopleIcon         from '@mui/icons-material/People';
import ShoppingCartIcon   from '@mui/icons-material/ShoppingCart';
import ShekelIcon         from '@mui/icons-material/CurrencyLira';
import DollarIcon         from '@mui/icons-material/AttachMoney';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import TaskAltIcon        from '@mui/icons-material/TaskAlt';
import Checkbox           from '@mui/material/Checkbox';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import authFetch from '../utils/authFetch';
import { ORDER_STATUS, badgeStyle, clickableA11y } from '../styles/ds';

const PIE_COLORS = ['#0d1d3e', '#c8a96e', '#2e7d32', '#1565c0', '#c62828'];
const NAVY = '#0d1d3e';

const fmt_ils = n => `₪${Number(n || 0).toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;
const fmt_usd = n => `$${Number(n || 0).toLocaleString('en-US',  { minimumFractionDigits: 0 })}`;
const fmtAxis = v => v >= 10000 ? `₪${(v / 1000).toFixed(0)}K` : `₪${v.toLocaleString('he-IL')}`;

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color, onClick }) => (
  <Card onClick={onClick}
    {...(onClick ? clickableA11y(onClick, { ariaLabel: `${label}: ${value}`, asButton: true }) : {})}
    sx={{
    cursor: onClick ? 'pointer' : 'default',
    transition: '0.2s',
    height: '100%',
    '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.11)' } : {},
  }}>
    <CardContent className="crm-card-content" sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, height: '100%', boxSizing: 'border-box' }}>
      <Avatar sx={{ width: 48, height: 48, backgroundColor: `${color}18`, color, borderRadius: 2, flexShrink: 0 }}>
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography variant="body2" color="text.secondary" fontSize={12} noWrap>{label}</Typography>
        <Typography variant="h5" fontWeight={800} color="text.primary" lineHeight={1.1} noWrap>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>{sub}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [urgentTasks,  setUrgentTasks]  = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    authFetch('/stats')
      .then(r => r.json())
      .then(data => { setStats(data); setLoading(false); })
      .catch(() => setLoading(false));
    authFetch('/tasks/urgent')
      .then(r => r.json())
      .then(d => setUrgentTasks(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const markTaskDone = async (id) => {
    await authFetch(`/tasks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'done' }),
    });
    setUrgentTasks(ts => ts.filter(t => t.id !== id));
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );
  if (!stats) return (
    <Box p={4}><Typography color="error">לא ניתן לטעון את הנתונים.</Typography></Box>
  );

  const pieData = Object.entries(stats.orders_by_status).map(([status, count]) => ({
    name:  ORDER_STATUS[status]?.label || status,
    value: count,
  }));
  const monthly = stats.monthly_revenue || [];

  return (
    <Box className="crm-page" sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>

      <Typography variant="h5" fontWeight={700} mb={3} color={NAVY}>לוח בקרה</Typography>

      {/* ── Stat cards — 4 equal columns ── */}
      <Box className="crm-dashboard-stats" sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}>
        <StatCard icon={<PeopleIcon />}       label="לקוחות"    value={stats.total_customers}     color="#0284c7" onClick={() => navigate('/customers')} />
        <StatCard icon={<ShoppingCartIcon />} label="הזמנות"    value={stats.total_orders}         color={NAVY}   onClick={() => navigate('/orders')} />
        <StatCard icon={<ShekelIcon />}       label="הכנסות ₪"  value={fmt_ils(stats.revenue_ils)} color="#16a34a" sub="ללא מבוטלות" />
        <StatCard icon={<DollarIcon />}       label="הכנסות $"  value={fmt_usd(stats.revenue_usd)} color="#2563eb" sub="ללא מבוטלות" />
      </Box>

      {/* ── Charts row — 7/12 + 5/12 ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
        gap: 2.5,
        mb: 2.5,
      }}>
        {/* Bar chart: monthly revenue */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>הכנסות חודשיות ₪</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              סה"כ לפי חודש (ללא מבוטלות)
            </Typography>
            {monthly.length === 0
              ? <Typography color="text.secondary" fontSize={13} py={4} textAlign="center">אין נתונים להצגה</Typography>
              : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={monthly} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} width={70}
                      axisLine={false} tickLine={false}
                      domain={[0, d => Math.ceil(d * 1.2) || 100]} />
                    <Tooltip formatter={v => [fmt_ils(v), 'הכנסות']}
                      contentStyle={{ borderRadius: 8, fontSize: 13, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
                    <Bar dataKey="ils" fill={NAVY} radius={[6, 6, 0, 0]} maxBarSize={60} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </CardContent>
        </Card>

        {/* Pie chart: orders by status */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>הזמנות לפי סטטוס</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>התפלגות עדכנית</Typography>
            {pieData.length === 0
              ? <Typography color="text.secondary" fontSize={13} py={4} textAlign="center">אין הזמנות</Typography>
              : (
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="45%" innerRadius={52} outerRadius={82}
                      dataKey="value" paddingAngle={2}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v + ' הזמנות', n]}
                      contentStyle={{ borderRadius: 8, fontSize: 13, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
                    <Legend iconType="circle" iconSize={9}
                      formatter={v => <span style={{ fontSize: 12, color: '#555' }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )
            }
          </CardContent>
        </Card>
      </Box>

      {/* ── Urgent tasks ── */}
      {urgentTasks.length > 0 && (
        <Card sx={{ mb: 2.5 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <TaskAltIcon sx={{ color: '#c62828', fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={700}>משימות דחופות</Typography>
              </Box>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/tasks')}>
                כל המשימות ←
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {urgentTasks.map(t => {
                const parseDueDate = (iso) => {
                  if (!iso) return null;
                  const s = String(iso).split('T')[0];
                  const [y, m, d] = s.split('-').map(Number);
                  return (y && m && d) ? new Date(y, m - 1, d) : null;
                };
                const dueDate = parseDueDate(t.due_date);
                const due = dueDate ? dueDate.toLocaleDateString('he-IL') : null;
                const isOverdue = dueDate && dueDate < new Date().setHours(0,0,0,0);
                return (
                  <Box key={t.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    border: '1px solid', borderColor: isOverdue ? '#fca5a5' : '#fde68a',
                    borderRadius: 2, px: 1.5, py: 0.8, backgroundColor: isOverdue ? '#fef2f2' : '#fffbeb',
                    minWidth: 200, flex: '1 1 200px', maxWidth: 320,
                  }}>
                    <Checkbox size="small" sx={{ p: 0.5 }} checked={false} onChange={() => markTaskDone(t.id)} />
                    <Box flex={1} minWidth={0}>
                      <Typography fontSize={13} fontWeight={600} noWrap>{t.title}</Typography>
                      {due && <Typography fontSize={11} color={isOverdue ? '#c62828' : '#92400e'}>
                        {isOverdue ? '⚠ פג תוקף: ' : '📅 '}{due}
                      </Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Bottom row — 3 columns ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 2.5,
      }}>
        {/* Recent orders */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>הזמנות אחרונות</Typography>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/orders')}>
                צפה בכל ←
              </Typography>
            </Box>
            {stats.recent_orders.length === 0
              ? <Typography color="text.secondary" fontSize={13} textAlign="center" py={3}>אין הזמנות עדיין.</Typography>
              : stats.recent_orders.map((order, i) => (
                <Box key={order.id}>
                  {i > 0 && <Divider sx={{ my: 1 }} />}
                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer', borderRadius: 1.5, px: 1, py: 0.5,
                    '&:hover': { backgroundColor: 'rgba(13,29,62,0.04)' },
                  }}
                    onClick={() => navigate(`/orders/${order.id}`)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700,
                        backgroundColor: 'rgba(13,29,62,0.1)', color: NAVY }}>
                        {`${order.first_name?.[0] ?? ''}${order.last_name?.[0] ?? ''}`.toUpperCase() || '#'}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={600} fontSize={13}>
                          #{order.id} — {order.first_name ?? ''} {order.last_name ?? ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize={11}>
                          {new Date(order.created_at).toLocaleDateString('he-IL')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontWeight={700} fontSize={13}>{fmt_ils(order.total_price_ils)}</Typography>
                      <span style={badgeStyle(ORDER_STATUS, order.status)}>
                        {ORDER_STATUS[order.status]?.label || order.status}
                      </span>
                    </Box>
                  </Box>
                </Box>
              ))
            }
          </CardContent>
        </Card>

        {/* Unpaid shipped orders */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <WarningAmberIcon sx={{ color: '#d97706', fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={700}>ממתינות לתשלום</Typography>
              </Box>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/orders')}>
                צפה בכל ←
              </Typography>
            </Box>
            {!stats.unpaid_shipped?.length
              ? <Typography color="text.secondary" fontSize={13} textAlign="center" py={3}>אין הזמנות פתוחות לגבייה</Typography>
              : stats.unpaid_shipped.map((order, i) => (
                <Box key={order.id}>
                  {i > 0 && <Divider sx={{ my: 1 }} />}
                  <Box
                    onClick={() => navigate(`/orders/${order.id}`)}
                    sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      cursor: 'pointer', borderRadius: 1.5, px: 1, py: 0.5,
                      '&:hover': { backgroundColor: 'rgba(217,119,6,0.06)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700,
                        backgroundColor: 'rgba(217,119,6,0.12)', color: '#92400e' }}>
                        {`${order.first_name?.[0] ?? ''}${order.last_name?.[0] ?? ''}`.toUpperCase() || '#'}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={600} fontSize={13}>
                          #{order.id} — {order.first_name ?? ''} {order.last_name ?? ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize={11}>
                          {new Date(order.created_at).toLocaleDateString('he-IL')} ·{' '}
                          <span style={{ color: order.status === 'delivered' ? '#15803d' : '#1d4ed8', fontWeight: 600 }}>
                            {order.status === 'delivered' ? 'נמסר' : 'נשלח'}
                          </span>
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography fontWeight={700} fontSize={13} color="#c2410c">
                        {fmt_ils(order.balance_ils)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={10}>
                        מתוך {fmt_ils(order.total_price_ils)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            }
          </CardContent>
        </Card>

        {/* Low stock alerts (variants below their min_stock threshold) */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <WarningAmberIcon sx={{ color: '#c62828', fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={700}>מלאי מתחת למינימום</Typography>
              </Box>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/inventory')}>
                למצב מלאי ←
              </Typography>
            </Box>
            {!stats.low_stock?.length
              ? <Typography color="text.secondary" fontSize={13} textAlign="center" py={3}>
                  אין פריטים מתחת לסף המינימום 🎉
                </Typography>
              : stats.low_stock.map((v, i) => (
                <Box key={v.id}>
                  {i > 0 && <Divider sx={{ my: 1 }} />}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
                    <Typography fontWeight={600} fontSize={13}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                      {v.name || `וריאנט #${v.id}`}
                    </Typography>
                    <Typography fontWeight={700} fontSize={12}
                      color={Number(v.quantity) === 0 ? '#c62828' : '#856404'}>
                      {v.quantity} / מינ' {v.min_stock}
                    </Typography>
                  </Box>
                </Box>
              ))
            }
          </CardContent>
        </Card>

        {/* Bar chart: monthly orders count */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} mb={0.5}>הזמנות לפי חודש</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>6 חודשים אחרונים</Typography>
            {monthly.length === 0
              ? <Typography color="text.secondary" fontSize={13} py={4} textAlign="center">אין נתונים</Typography>
              : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false}
                      domain={[0, d => Math.ceil(d * 1.3) || 5]} />
                    <Tooltip formatter={v => [v, 'הזמנות']}
                      contentStyle={{ borderRadius: 8, fontSize: 13, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
                    <Bar dataKey="orders" fill="#c8a96e" radius={[6, 6, 0, 0]} maxBarSize={60} />
                  </BarChart>
                </ResponsiveContainer>
              )
            }
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
